import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const AUTH = {
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
  },
};

const SEASON = 2026;

function isFinal(status, statusState) {
  const s = `${status || ""} ${statusState || ""}`.toLowerCase();
  return s.includes("final");
}

function SchedulePage() {
  const [games, setGames] = useState([]);
  const [weeks, setWeeks] = useState([1]);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [loading, setLoading] = useState(true);

  // navbar (same pattern as standings)
  const [openDropdown, setOpenDropdown] = useState(null);
  const teamsRef = useRef(null);
  const leadersRef = useRef(null);
  const playersRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [navbarSearchTerm, setNavbarSearchTerm] = useState("");
  const [navbarResults, setNavbarResults] = useState([]);
  const navbarInputRef = useRef(null);
  const navbarContainerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navbarContainerRef.current && !navbarContainerRef.current.contains(e.target)) {
        setSearchExpanded(false);
        setNavbarResults([]);
      }
      if (
        teamsRef.current && !teamsRef.current.contains(e.target) &&
        leadersRef.current && !leadersRef.current.contains(e.target) &&
        playersRef.current && !playersRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!navbarSearchTerm.trim()) {
      setNavbarResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-players?search=${encodeURIComponent(navbarSearchTerm)}`,
          AUTH
        );
        setNavbarResults((res.data || []).slice(0, 12));
      } catch (err) {
        console.error(err);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [navbarSearchTerm]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-season-schedule?season=${SEASON}`,
          AUTH
        );
        const payload = res.data || {};
        const list = payload.games || [];
        const wk = payload.weeks?.length ? payload.weeks : [1];
        const cur = payload.currentWeek || wk[0] || 1;

        setGames(list);
        setWeeks(wk);
        setCurrentWeek(cur);
        setSelectedWeek(cur);
      } catch (err) {
        console.error("Schedule error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const weekGames = games
    .filter((g) => Number(g.week) === Number(selectedWeek))
    .sort((a, b) => new Date(a.game_date) - new Date(b.game_date));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-3xl text-white">
        Loading schedule...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans">
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-md z-50 border-b border-red-500/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/images/HOFLogoSquare.png" alt="HOF Logo" className="w-12 h-12 object-contain" />
            <div>
              <div className="text-2xl font-black tracking-tighter">HELMETS OFF</div>
              <div className="text-[10px] text-red-400 -mt-1">FOOTBALL</div>
            </div>
          </Link>
          <div className="flex-1 hidden md:block" />
          <div className="flex items-center gap-4">
            <div className="relative" ref={navbarContainerRef}>
              {!searchExpanded ? (
                <button
                  onClick={() => {
                    setSearchExpanded(true);
                    setTimeout(() => navbarInputRef.current?.focus(), 50);
                  }}
                  className="p-2 text-zinc-400 hover:text-white"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              ) : (
                <div className="relative w-64">
                  <input
                    ref={navbarInputRef}
                    value={navbarSearchTerm}
                    onChange={(e) => setNavbarSearchTerm(e.target.value)}
                    placeholder="Search..."
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-full py-3 px-5 text-sm focus:outline-none focus:border-red-500"
                    autoFocus
                  />
                  {navbarResults.length > 0 && (
                    <div className="absolute mt-2 w-full bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden z-50 max-h-64 overflow-y-auto">
                      {navbarResults.map((item) => (
                        <Link
                          key={`${item.type}-${item.bdl_id || item.id}`}
                          to={item.type === "team" ? `/team/${item.abbreviation}` : `/player/${item.bdl_id || item.id}`}
                          className="block px-4 py-3 hover:bg-zinc-800"
                          onClick={() => { setNavbarSearchTerm(""); setNavbarResults([]); setSearchExpanded(false); }}
                        >
                          {item.type === "team"
                            ? item.name
                            : `${item.first_name} ${item.last_name}`}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="hidden md:flex items-center gap-8 text-sm font-medium">
              <div className="relative" ref={teamsRef}>
                <button onClick={() => setOpenDropdown(openDropdown === "teams" ? null : "teams")} className="hover:text-red-400">TEAMS</button>
                {openDropdown === "teams" && (
                  <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden z-50">
                    <Link to="/teams" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>All Teams</Link>
                    <Link to="/standings" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Standings</Link>
                    <Link to="/schedule" className="block px-5 py-3 hover:bg-zinc-800 text-red-400" onClick={() => setOpenDropdown(null)}>Schedule</Link>
                  </div>
                )}
              </div>
              <div className="relative" ref={leadersRef}>
                <button onClick={() => setOpenDropdown(openDropdown === "leaders" ? null : "leaders")} className="hover:text-red-400">LEADERS</button>
                {openDropdown === "leaders" && (
                  <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden z-50">
                    <Link to="/leaderboards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Season Leaders</Link>
                  </div>
                )}
              </div>
              <div className="relative" ref={playersRef}>
                <button onClick={() => setOpenDropdown(openDropdown === "players" ? null : "players")} className="hover:text-red-400">PLAYERS</button>
                {openDropdown === "players" && (
                  <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden z-50">
                    <Link to="/playerStats" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Player Stats</Link>
                    <Link to="/playerCompare" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Player Compare</Link>
                  </div>
                )}
              </div>
            </div>
            <button className="md:hidden p-2 text-zinc-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-black/95 border-b border-zinc-800 px-6 py-5 z-40">
            <Link to="/schedule" className="block py-3 text-red-400" onClick={() => setMobileMenuOpen(false)}>Schedule</Link>
            <Link to="/standings" className="block py-3" onClick={() => setMobileMenuOpen(false)}>Standings</Link>
            <Link to="/teams" className="block py-3" onClick={() => setMobileMenuOpen(false)}>Teams</Link>
            <Link to="/leaderboards" className="block py-3" onClick={() => setMobileMenuOpen(false)}>Leaders</Link>
          </div>
        )}
      </nav>

      <div className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-black text-center mb-3 tracking-tighter">
            SEASON SCHEDULE
          </h1>
          <p className="text-center text-zinc-500 mb-8">{SEASON} Regular Season</p>

          <div className="flex justify-center mb-10">
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(Number(e.target.value))}
              className="bg-zinc-900 border border-zinc-700 rounded-xl px-6 py-3 text-lg focus:outline-none focus:border-red-500"
            >
              {weeks.map((w) => (
                <option key={w} value={w}>
                  Week {w}{Number(w) === Number(currentWeek) ? " · Current" : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-zinc-900 rounded-3xl shadow-2xl overflow-hidden border border-zinc-800">
            <div className="p-6 md:p-8">
              <h2 className="text-2xl font-black text-center mb-8">Week {selectedWeek}</h2>

              {weekGames.length === 0 ? (
                <div className="text-center py-16 text-zinc-500">No games found for this week</div>
              ) : (
                <div className="space-y-2.5">
                  {weekGames.map((g) => {
                    const final = isFinal(g.status, g.status_state);
                    const dateStr = g.game_date
                      ? new Date(g.game_date).toLocaleString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : "TBD";

                    return (
                      <div
  key={g.bdl_id}
  className="bg-zinc-800 rounded-2xl px-5 py-3 md:px-6 md:py-3.5 flex items-center justify-between gap-4"
>
  <div className="font-bold text-base md:text-lg min-w-0">
    <Link to={`/team/${g.visitor_team_abbr}`} className="hover:text-red-400">
      {g.visitor_team_abbr}
    </Link>
    <span className="text-zinc-500 mx-2">@</span>
    <Link to={`/team/${g.home_team_abbr}`} className="hover:text-red-400">
      {g.home_team_abbr}
    </Link>
  </div>

  <div className="text-right shrink-0 leading-tight">
    {final && (
      <div className="text-xl font-black mb-0.5">
        {g.visitor_score ?? 0} - {g.home_score ?? 0}
      </div>
    )}
    <div className="text-sm text-zinc-400">{dateStr}</div>
    {g.venue && <div className="text-xs text-zinc-500 mt-0.5">{g.venue}</div>}
  </div>
</div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-black border-t border-zinc-800 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center text-zinc-500">
          <p>© 2026 Helmets Off Football • Serratox</p>
        </div>
      </footer>
    </div>
  );
}

export default SchedulePage;